import React, { useState } from 'react';
import { toast } from 'sonner';
import { postEnquiry } from '../../api';
import { logError } from '../../lib/logger';
import Reveal from '../Reveal';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const fieldClass =
  'border-white/15 bg-white/[0.04] text-neutral-100 placeholder:text-neutral-500 focus-visible:ring-gold/60';

const emptyForm = { org: '', name: '', email: '', phone: '', message: '' };

const validatePartner = (f) => {
  if (!f.org.trim() || !f.name.trim() || !f.email.trim()) return 'Please fill in organisation, contact name and email.';
  if (!/^\S+@\S+\.\S+$/.test(f.email)) return 'Please enter a valid email address.';
  return null;
};

const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[10px] tracking-[0.25em] text-neutral-400">
      {label}
    </Label>
    {children}
  </div>
);

const LogoPair = () => (
  <div className="mt-10 flex items-center gap-6">
    <div className="flex flex-col items-center gap-3">
      <img
        src="/assets/khm-cares.png"
        alt="KHM Cares"
        className="h-24 w-24 rounded-full object-cover shadow-[0_15px_50px_rgba(227,167,43,0.2)] md:h-28 md:w-28"
      />
      <p className="text-[9px] tracking-[0.3em] text-neutral-500">KHM CARES</p>
    </div>
    <span className="font-serif text-2xl font-light text-gold">×</span>
    <div className="flex flex-col items-center gap-3">
      <span className="flex items-center bg-white px-5 py-4 shadow-[0_15px_50px_rgba(0,0,0,0.45)]">
        <img src="/assets/khm-events.png" alt="KHM Events Pakistan" className="h-10 w-auto md:h-12" />
      </span>
      <p className="text-[9px] tracking-[0.3em] text-neutral-500">KHM EVENTS</p>
    </div>
  </div>
);

const PartnerIntro = ({ partner }) => (
  <div>
    <Reveal>
      <p className="mb-5 text-[10px] uppercase tracking-[0.5em] text-gold">{partner.label}</p>
    </Reveal>
    <Reveal delay={120}>
      <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-[0.03em] text-white md:text-5xl">
        {partner.title}
      </h2>
    </Reveal>
    <Reveal delay={220}>
      <div className="mt-6 h-px w-12 bg-gold" />
    </Reveal>
    <Reveal delay={300}>
      <p className="mt-8 max-w-md text-base leading-relaxed text-neutral-300">{partner.text}</p>
    </Reveal>
    <Reveal delay={380}>
      <LogoPair />
    </Reveal>
  </div>
);

const PartnerForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const error = validatePartner(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      await postEnquiry('partner', {
        Organisation: form.org,
        'Contact name': form.name,
        Email: form.email,
        Phone: form.phone || '—',
        Message: form.message || '—',
      });
      setForm(emptyForm);
      toast.success('Partnership request sent', {
        description: 'The KHM Cares team will reach out to explore creating impact together.',
      });
    } catch (err) {
      logError('Partner request failed:', err);
      toast.error('Could not send your request', {
        description: 'Please check your connection and try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-white/10 bg-white/[0.03] p-6 md:p-9">
      <p className="mb-1 text-[10px] uppercase tracking-[0.4em] text-gold">BECOME A PARTNER</p>
      <p className="mb-7 text-[11px] leading-relaxed text-neutral-500">
        Tell us who you are and how you'd like to create impact together.
      </p>
      <div className="space-y-5">
        <Field id="pt-org" label="ORGANISATION *">
          <Input id="pt-org" value={form.org} onChange={set('org')} placeholder="Your organisation or brand" className={fieldClass} />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field id="pt-name" label="CONTACT NAME *">
            <Input id="pt-name" value={form.name} onChange={set('name')} placeholder="Full name" className={fieldClass} />
          </Field>
          <Field id="pt-email" label="EMAIL *">
            <Input id="pt-email" type="email" value={form.email} onChange={set('email')} placeholder="you@company.com" className={fieldClass} />
          </Field>
        </div>
        <Field id="pt-phone" label="PHONE">
          <Input id="pt-phone" value={form.phone} onChange={set('phone')} placeholder="+92 ..." className={fieldClass} />
        </Field>
        <Field id="pt-message" label="HOW WOULD YOU LIKE TO PARTNER?">
          <Textarea
            id="pt-message"
            value={form.message}
            onChange={set('message')}
            placeholder="Sponsorship, venue support, matched giving, event collaboration..."
            rows={4}
            className={fieldClass}
          />
        </Field>
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-gold py-4 text-[11px] tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
        >
          {submitting ? 'SENDING...' : 'SUBMIT PARTNERSHIP REQUEST'}
        </button>
      </div>
    </form>
  );
};

export const CsrPartner = ({ partner }) => (
  <section className="bg-ink py-16 md:py-20">
    <div className="mx-auto grid max-w-[1400px] gap-14 px-6 md:px-10 lg:grid-cols-2 lg:gap-16">
      <PartnerIntro partner={partner} />
      <Reveal delay={200}>
        <PartnerForm />
      </Reveal>
    </div>
  </section>
);
